import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { refusedByTheApi, type SignupRequest } from "@shyne/shared/api";
import { Button } from "@/components/ui/button";
import { FormCreator, type FormFieldDef } from "@/components/shared/form-creator";
import { useAppDispatch, useAppSelector } from "@/lib/store";
import { logoutUser } from "@/lib/store/auth.slice.ts";
import { finishSignup, pendingSignup } from "@/app/auth/signup/signup.api.ts";
import { refreshSignedIn, settleIntoOrganization } from "@/app/auth/signup/signup.slice.ts";
import type { PendingSignup } from "@/repositories/signup.repository.ts";

const finishSchema = z.object({
  organizationKind: z.enum(["customer", "vendor", "subcontractor"]),
  organizationName: z.string().trim().min(1),
  name: z.string().trim().min(2),
  phone: z.string().trim().optional(),
});

type FinishValues = z.infer<typeof finishSchema>;

const fields: FormFieldDef[] = [
  {
    type: "select",
    name: "organizationKind",
    label: "Organization type",
    placeholder: "Select Organization Type",
    required: true,
    options: [
      { value: "customer", label: "Property Manager" },
      { value: "vendor", label: "Vendor" },
      { value: "subcontractor", label: "Sub-contractor" },
    ],
  },
  { type: "text", name: "organizationName", label: "Organization Name", required: true },
  { type: "text", name: "name", label: "Full name", required: true },
  { type: "text", name: "phone", label: "Phone" },
];

const submittedFromDetailsOf = new Set<string>();

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-svh items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-4">{children}</div>
    </div>
  );
}

export default function FinishSignupPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const signupPending = useAppSelector((state) => state.auth.signupPending);
  const [pending, setPending] = useState<PendingSignup | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const form = useForm<FinishValues>({ resolver: zodResolver(finishSchema), defaultValues: { organizationName: "", name: "", phone: "" } });

  const finish = useCallback(async (details: SignupRequest) => {
    setSaving(true);
    setFailure(null);
    try {
      const { organization } = await finishSignup(details);
      await dispatch(settleIntoOrganization(organization));
      navigate("/");
    } catch (error: unknown) {
      if (refusedByTheApi(error) && error.status === 409) {
        await dispatch(refreshSignedIn());
        navigate("/");
        return;
      }
      setFailure(error instanceof Error ? error.message : "Signing up could not be finished.");
    } finally {
      setSaving(false);
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    if (!signupPending) return;
    pendingSignup()
      .then((found) => {
        setPending(found);
        if (found.kind === "incomplete" || found.kind === "ready") form.reset({ organizationKind: found.details.organizationKind, organizationName: found.details.organizationName ?? "", name: found.details.name ?? "", phone: found.details.phone ?? "" });
        if (found.kind === "ready" && !submittedFromDetailsOf.has(found.email)) {
          submittedFromDetailsOf.add(found.email);
          void finish(found.details);
        }
      })
      .catch((error: unknown) => setFailure(error instanceof Error ? error.message : "Signing up could not be finished."));
  }, [signupPending, finish, form]);

  if (!signupPending) return <Navigate to="/login" />;

  const signOut = (
    <Button variant="ghost" className="w-full" onClick={() => dispatch(logoutUser())}>
      Sign out
    </Button>
  );

  if (pending?.kind === "invited") {
    return (
      <Centered>
        <h1 className="text-2xl font-bold">Finish signing up</h1>
        <p className="text-sm text-muted-foreground">
          You were invited to Shyne. Open the invitation link in the email sent to {pending.email} to finish signing up.
        </p>
        {signOut}
      </Centered>
    );
  }

  if (pending?.kind === "exists") {
    return (
      <Centered>
        <h1 className="text-2xl font-bold">Finish signing up</h1>
        <p className="text-sm text-muted-foreground">An account with {pending.email} already exists. Sign out and log in with it instead.</p>
        {signOut}
      </Centered>
    );
  }

  if (pending?.kind === "incomplete" || (pending?.kind === "ready" && failure)) {
    return (
      <Centered>
        <div className="flex flex-col items-start gap-2">
          <h1 className="text-2xl font-bold">Finish signing up</h1>
          <p className="text-sm text-muted-foreground">Tell us about your organization to finish setting up {pending.email}.</p>
        </div>
        {failure && <p className="text-sm text-destructive">{failure}</p>}
        <FormCreator
          form={form}
          formDefinition={fields}
          buttonText={saving ? "Finishing…" : "Finish signing up"}
          onSubmit={(values: FinishValues) => finish({ ...values, phone: values.phone || undefined })}
        />
        {signOut}
      </Centered>
    );
  }

  return (
    <Centered>
      {failure ? <p className="text-sm text-destructive">{failure}</p> : <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />}
      {failure && signOut}
    </Centered>
  );
}
